import { cn } from '@/lib/cn';
import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type HoverTooltipProps = {
  label: string;
  enabled?: boolean;
  children: React.ReactNode;
  className?: string;
};

export function HoverTooltip({
  label,
  enabled = true,
  children,
  className,
}: HoverTooltipProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!visible || !anchorRef.current) return;

    function updatePosition() {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 10,
      });
    }

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [visible]);

  if (!enabled) {
    return <div className={className}>{children}</div>;
  }

  return (
    <>
      <div
        ref={anchorRef}
        className={className}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
      >
        {children}
      </div>
      {visible &&
        position &&
        createPortal(
          <div
            role="tooltip"
            style={{ top: position.top, left: position.left }}
            className={cn(
              'pointer-events-none fixed z-[300] -translate-y-1/2 whitespace-nowrap rounded-lg',
              'border border-manejai-sand/80 bg-manejai-forest px-2.5 py-1.5 text-xs font-medium text-white shadow-md',
            )}
          >
            {label}
          </div>,
          document.body,
        )}
    </>
  );
}
